import { useState, useEffect } from 'react';
import { PhotoAsset } from '../types.ts/story';
import { searchPhotos, getAttributionForPhoto } from '../utility/unsplash';

export const useUnsplashSearch = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [results, setResults] = useState<PhotoAsset[]>([]);

  const searchUnsplashPhotos = async (query: string, page: number = 1) => {
    if (!query.trim()) {
      setResults([]);
      return [];
    }

    try {
      setIsLoading(true);
      
      const { photos } = await searchPhotos({
        query,
        orientation: 'landscape',
        page,
      });

      const photoAssets = (photos?.map((photo) => ({
        id: photo.id,
        title: photo.description,
        description: photo.description,
        src: photo.urls?.regular,
        type: 'stock',
        alt: photo.alt_description,
        slug: `${(photo.alt_description || photo.description)
          ?.toLowerCase()
          ?.split(' ')
          .join('_')}_${photo.id}`,
        attribution: getAttributionForPhoto(photo),
      })) || []) as PhotoAsset[];

      return photoAssets;
    } catch (error) {
      console.error('Failed to search Unsplash photos:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    setSearchTerm(query);
    setCurrentPage(1);
    
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const photos = await searchUnsplashPhotos(query, 1);
    setResults(photos);
  };

  const loadMorePhotos = async () => {
    if (!searchTerm || isLoading) return;

    const nextPage = currentPage + 1;
    const newPhotos = await searchUnsplashPhotos(searchTerm, nextPage);
    
    setResults(prev => [...prev, ...newPhotos]);
    setCurrentPage(nextPage);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setResults([]);
  };

  // Auto-search effect
  useEffect(() => {
    if (searchTerm) {
      handleSearch(searchTerm);
    }
  }, [searchTerm]);

  return {
    isLoading,
    searchTerm,
    results,
    currentPage,
    handleSearch,
    loadMorePhotos,
    clearSearch,
  };
};